import { i18n } from '@/i18n/i18n'
import { ErrorService } from '@/shared/service/errorService'
import { SnackbarService } from '@/shared/snackbar/snackbar.service'

import { createStore, type GettersObj } from '../../utils/createStore'
import { NewsArticleService } from './newsArticle.service'
import { type NewsArticle } from './newsArticle.type'

export type GettersNewsArticles = GettersObj<typeof mapGettersNewsArticles>

export class NewsArticlesState {
  articles: NewsArticle[] = []
  deletingArticle: NewsArticle | null = null
  loading = false
}

export function titleOf(article: NewsArticle): string {
  return article.titles.fr || article.titles.en || ''
}

export const {
  newsArticles,
  commit: commitNewsArticles,
  dispatch: dispatchNewsArticles,
  mapGetters: mapGettersNewsArticles,
  mapState: mapStateNewsArticles,
  useState: useStateNewsArticles,
  useGetter: useGetterNewsArticles,
} = createStore({
  moduleName: 'newsArticles',
  initState: new NewsArticlesState(),
  mutations: {
    LOAD(state) {
      state.loading = true
    },

    UNLOAD(state) {
      state.loading = false
    },

    SET_ARTICLES(state, articles: NewsArticle[]) {
      state.articles = articles
    },

    REPLACE_ARTICLE(state, article: NewsArticle) {
      state.articles = state.articles.map((candidate) =>
        candidate._id === article._id ? article : candidate,
      )
    },

    SET_DELETING(state, article: NewsArticle | null) {
      state.deletingArticle = article
    },
  },
  actions: {
    async list({ commit }) {
      commit('LOAD')
      try {
        commit('SET_ARTICLES', await NewsArticleService.getAll())
      } catch (e) {
        ErrorService.handleError(e)
      } finally {
        commit('UNLOAD')
      }
    },

    async toggleVisible({ commit }, article: NewsArticle) {
      try {
        const updated = await NewsArticleService.setVisible(
          article._id,
          !article.visible,
        )
        commit('REPLACE_ARTICLE', updated)
        SnackbarService.info(
          i18n.tc(
            updated.visible
              ? 'NEWS_ARTICLES.SNACK_BAR.SHOW'
              : 'NEWS_ARTICLES.SNACK_BAR.HIDE',
            undefined,
            { name: titleOf(updated) },
          ),
        )
      } catch (e) {
        ErrorService.handleError(e)
      }
    },

    async deleteArticle({ state, commit, dispatch }) {
      const article = state.deletingArticle
      if (article == null) {
        return
      }
      commit('LOAD')
      try {
        await NewsArticleService.delete(article._id)
        SnackbarService.info(
          i18n.tc('NEWS_ARTICLES.SNACK_BAR.DELETE', undefined, {
            name: titleOf(article),
          }),
        )
        await dispatch('list')
      } catch (e) {
        ErrorService.handleError(e)
      } finally {
        commit('SET_DELETING', null)
        commit('UNLOAD')
      }
    },
  },
  getters: {
    // Pinned first, then most recent, as the mobile app lists them.
    getSortedArticles: (state) =>
      [...state.articles].sort((a, b) => {
        if (a.pinned !== b.pinned) return a.pinned ? -1 : 1
        return (b.publishedDate ?? '').localeCompare(a.publishedDate ?? '')
      }),
  },
})
